import React from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Stack,
  IconButton,
  Button,
  LinearProgress,
  styled,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowUpRight,
  ArrowDownRight,
  Phone,
  Users,
  PhoneIncoming,
  PhoneOff,
  Clock,
  MessageSquare,
  BarChart2,
  MoreVertical,
  CheckCircle2,
  AlertCircle,
  Copy
} from 'lucide-react';

// --- Styled Components ---

const GlassCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(12px)',
  borderRadius: '20px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
  border: '1px solid #F0F0F5',
  padding: '24px',
  transition: 'box-shadow 0.3s ease, transform 0.2s ease',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
    transform: 'translateY(-1px)',
  },
}));

const GradientCard = styled(Box)(({ theme, gradient }) => ({
  background: gradient || 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
  borderRadius: '20px',
  padding: '32px',
  [theme.breakpoints.down('sm')]: {
    padding: '24px 20px',
  },
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-20%',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-30%',
    left: '10%',
    width: '250px',
    height: '250px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.03)',
    pointerEvents: 'none',
  },
}));

const IconWrapper = styled(Box)(({ theme, bgcolor }) => ({
  backgroundColor: bgcolor || '#F3F4F6',
  borderRadius: '14px',
  padding: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// --- Sub-components ---

export const StatCard = ({ title, value, icon: Icon, color }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <GlassCard sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500} gutterBottom sx={{ fontSize: { xs: '11px', sm: '13px' }, letterSpacing: '0.2px' }}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ my: { xs: 0.5, sm: 1.2 }, fontSize: { xs: '1.4rem', sm: '2rem' }, color: '#111827' }}>
            {value != null && !isNaN(Number(value)) ? Number(value).toLocaleString() : (value || '0')}
          </Typography>
        </Box>
        <IconWrapper bgcolor={`${color}12`} sx={{ color: color, p: { xs: 1, sm: 1.5 } }}>
          <Icon size={isMobile ? 20 : 24} />
        </IconWrapper>
      </Stack>
    </GlassCard>
  );
};

export const AccountOverview = ({ companyName, customerCareNo, expiryDate, subscriptionStatus, subscriptionStart }) => {
  const [timeLeft, setTimeLeft] = React.useState({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00'
  });
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!expiryDate || subscriptionStatus === 'yettostart') return;

    const timer = setInterval(() => {
      const target = new Date(expiryDate).getTime();
      const now = new Date().getTime();
      const distance = target - now;

      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({ days: '00', hours: '00', minutes: '00', seconds: '00' });
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft({
        days: days.toString().padStart(2, '0'),
        hours: hours.toString().padStart(2, '0'),
        minutes: minutes.toString().padStart(2, '0'),
        seconds: seconds.toString().padStart(2, '0')
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [expiryDate, subscriptionStatus]);

  const handleCopy = () => {
    if (customerCareNo) {
      navigator.clipboard.writeText(customerCareNo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isYetToStart = subscriptionStatus === 'yettostart';

  return (
    <GlassCard sx={{ p: { xs: 3, md: 4 }, position: 'relative' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center" justifyContent="space-between">
        <Box flex={1}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <Box sx={{ color: '#4F46E5' }}>
              <CheckCircle2 size={18} />
            </Box>
            <Typography variant="overline" fontWeight={700} color="#4F46E5" letterSpacing={1.5} sx={{ fontSize: '11px' }}>
              ACCOUNT OVERVIEW
            </Typography>
          </Stack>
          <Typography variant="h3" fontWeight={800} sx={{ mb: 3, color: '#111827', fontSize: { xs: '1.6rem', md: '2rem' }, lineHeight: 1.3 }}>
            Welcome back, {companyName}!
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              bgcolor: '#F9FAFB',
              px: 2.5,
              py: 1.5,
              borderRadius: '14px',
              border: '1px solid #E5E7EB',
              gap: { xs: 1.5, sm: 0 }
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Phone size={18} style={{ color: '#4F46E5' }} />
              <Typography variant="body1" fontWeight={500} sx={{ fontSize: '14px' }}>
                Your Customer Care Number: <Box component="span" sx={{ fontWeight: 700 }}>{customerCareNo}</Box>
              </Typography>
            </Stack>
            <Button
              size="small"
              onClick={handleCopy}
              startIcon={<Copy size={14} />}
              sx={{
                ml: 2,
                textTransform: 'none',
                color: copied ? '#10B981' : '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '12px',
                px: 1.5,
                minWidth: 'auto',
                '&:hover': { bgcolor: '#F3F4F6', borderColor: '#D1D5DB' }
              }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: isYetToStart ? '#F3F4F6' : '#FFF7ED',
            p: { xs: 2.5, md: 3 },
            borderRadius: '20px',
            border: isYetToStart ? '1px solid #E5E7EB' : '1px solid #FFEDD5',
            minWidth: { md: '380px' },
            width: { xs: '100%', md: 'auto' }
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
              <Box sx={{ color: isYetToStart ? '#6B7280' : '#F97316', p: 1.2, bgcolor: 'white', borderRadius: '14px', border: isYetToStart ? '1px solid #E5E7EB' : '1px solid #FED7AA' }}>
                {isYetToStart ? <AlertCircle size={24} /> : <Clock size={24} />}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" fontWeight={800} color={isYetToStart ? '#4B5563' : '#9A3412'} sx={{ letterSpacing: 1.5, fontSize: '10px' }}>
                  {subscriptionStatus?.toUpperCase() || 'PLAN'} EXPIRY
                </Typography>
                <Stack
                  direction="row"
                  spacing={{ xs: 0.5, sm: 1 }}
                  sx={{
                    mt: 1,
                    flexWrap: 'nowrap',
                    justifyContent: 'flex-start',
                  }}
                  alignItems="center"
                >
                  {isYetToStart ? (
                    <Typography variant="h5" fontWeight={800} color="#111827" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                      Yet to Start
                    </Typography>
                  ) : (
                    [
                      { label: 'DAYS', value: timeLeft.days },
                      { label: 'HRS', value: timeLeft.hours },
                      { label: 'MIN', value: timeLeft.minutes },
                      { label: 'SEC', value: timeLeft.seconds }
                    ].map((item, idx) => (
                      <React.Fragment key={item.label}>
                        <Box textAlign="center">
                          <Box sx={{
                            bgcolor: 'white',
                            px: { xs: 1, sm: 1.5 },
                            py: 0.8,
                            borderRadius: '8px',
                            border: '1px solid #FED7AA',
                            mb: 0.3,
                            minWidth: { xs: '32px', sm: '38px' }
                          }}>
                            <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '14px', sm: '18px' }, color: '#111827' }}>{item.value}</Typography>
                          </Box>
                          <Typography variant="caption" fontWeight={700} sx={{ color: '#9A3412', fontSize: '9px' }}>{item.label}</Typography>
                        </Box>
                        {idx < 3 && <Typography variant="h6" sx={{ color: '#D97706', fontWeight: 700, mt: { xs: -2.5, sm: -1 } }}>:</Typography>}
                      </React.Fragment>
                    ))
                  )}
                </Stack>
              </Box>
            </Box>
            <Button
              variant="contained"
              fullWidth={true}
              sx={{
                bgcolor: isYetToStart ? '#4F46E5' : '#F97316',
                '&:hover': { bgcolor: isYetToStart ? '#4338CA' : '#EA580C' },
                borderRadius: '14px',
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                py: { xs: 1.5, md: 1.2 },
                fontSize: '13px',
                boxShadow: isYetToStart ? '0 4px 14px rgba(79, 70, 229, 0.35)' : '0 4px 14px rgba(249, 115, 22, 0.35)',
                whiteSpace: 'nowrap',
                mt: { xs: 1, sm: 0 },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              {isYetToStart ? 'Start Plan' : 'Renew Plan'}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </GlassCard>
  );
};

export const ChatOperationsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const chatStats = [
    { label: 'Total Interactions', value: '0', trend: '↗ 0% vs avg', trendColor: '#4F46E5' },
    { label: 'Resolved Chat', value: '0', trend: '↗ 0% rate', trendColor: '#10B981' },
    { label: 'Open Chat', value: '0', trend: '⏱ No pending', trendColor: '#F59E0B' }
  ];

  return (
    <GradientCard gradient="linear-gradient(135deg, #4F46E5 0%, #6366F1 40%, #3B82F6 100%)">
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 3, md: 4 }}
        sx={{ width: '100%', zIndex: 1 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Stack direction="row" spacing={3} alignItems="center" flex={1}>
          <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '16px', backdropFilter: 'blur(4px)' }}>
            <MessageSquare size={isMobile ? 24 : 32} />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.3rem', md: '1.8rem' } }}>Chat Operations</Typography>
            <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.5, maxWidth: '400px', fontSize: '13px', lineHeight: 1.5 }}>
              Real-time metrics for customer chat interactions and support agent availability.
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'row', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          sx={{
            width: { xs: '100%', md: 'auto' },
            overflowX: { xs: 'auto', sm: 'visible' },
            pb: { xs: 1, sm: 0 },
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        >
          {chatStats.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                bgcolor: 'rgba(255,255,255,0.95)',
                color: '#111827',
                p: { xs: 1.5, sm: 2.5 },
                borderRadius: '18px',
                minWidth: { xs: '120px', sm: '160px' },
                textAlign: 'center',
                backdropFilter: 'blur(8px)',
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'translateY(-2px)' }
              }}
            >
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: '10px' }}>
                {item.label}
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ my: 0.5, color: '#4F46E5', fontSize: { xs: '1.4rem', sm: '1.8rem' } }}>
                {item.value}
              </Typography>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: item.trendColor, fontSize: '10px' }}
              >
                {item.trend}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    </GradientCard>
  );
};
